/**
 * `surface-commands.ts` — a typed, tier-1 bus for "ask another surface to do a
 * thing" (#1527).
 *
 * WHY. The extraction design's rule 4 says cross-plugin dependence is inverted
 * through the platform tier, never a direct import. The worked case: the
 * home-battery rack projection asks the CAD surface to LOAD the document it just
 * built, and did so by importing `cad/cad-store.ts` and calling
 * `useCadStore.getState().loadDocument(...)`. One call, and it bound a domain app
 * to another domain app's live store.
 *
 * THE COMMAND HANDLE CARRIES THE TYPE, AND LIVES WITH THE DOMAIN — not here. This
 * module knows nothing about CAD; `defineSurfaceCommand<CadModel>('cad.loadDocument')`
 * is declared in `@ubiclouds/v5-cad-kernel`, which BOTH families already depend
 * on. So the payload stays type-checked end to end, and the platform tier does not
 * acquire a vocabulary of other people's domains — which is what a `Record<string,
 * unknown>` event bus would have cost.
 *
 * `invoke` RETURNS whether anything handled it, rather than throwing or silently
 * succeeding. An unhandled command is the normal state — the target surface may
 * simply not be mounted — and the caller usually wants to fall back, not crash.
 * Silently returning void would make "nobody was listening" and "it worked" the
 * same observation, which is the failure this repo keeps paying for.
 *
 * @module @ubiclouds/v5-workbench/surface-commands
 */

/** A typed command handle. The type parameter is the payload the handler receives. */
export interface SurfaceCommand<A> {
  readonly key: string;
  /** Phantom — carries `A` through the handle so register/invoke stay in agreement. */
  readonly __arg?: (arg: A) => void;
}

/** Declare a command. Call this ONCE, beside the domain type it carries. */
export function defineSurfaceCommand<A>(key: string): SurfaceCommand<A> {
  return { key };
}

const handlers = new Map<string, (arg: unknown) => void>();

/**
 * Register the handler for a command. Re-registering REPLACES, so a surface that
 * remounts converges rather than stacking handlers (the standing registry rule).
 * Returns an unregister fn for effect cleanup — a handler that outlives its
 * surface would call into a torn-down store.
 */
export function registerSurfaceCommand<A>(
  command: SurfaceCommand<A>,
  handler: (arg: A) => void,
): () => void {
  handlers.set(command.key, handler as (arg: unknown) => void);
  return () => {
    if (handlers.get(command.key) === (handler as (arg: unknown) => void)) {
      handlers.delete(command.key);
    }
  };
}

/**
 * Invoke a command. Returns `true` when a handler ran, `false` when none is
 * registered. Throw-safe: a handler that throws is contained and reported as
 * unhandled, because the caller is a different plugin and must not inherit
 * another surface's failure.
 */
export function invokeSurfaceCommand<A>(command: SurfaceCommand<A>, arg: A): boolean {
  const handler = handlers.get(command.key);
  if (!handler) return false;
  try {
    handler(arg);
    return true;
  } catch {
    return false;
  }
}

/** Test hook: forget every registered handler. */
export function resetSurfaceCommandsForTest(): void {
  handlers.clear();
}
